import type { JSX } from 'react';
import { Container } from '@components/ui/Container';
import { RevealOnView } from '@components/ui/RevealOnView';
import { SectionHeading } from '@components/ui/SectionHeading';
import { landingCopy } from '@features/landing/data/landing.pt';

const STAGGER_DELAY_MS = 130;

/**
 * Secao "Como funciona" (SECT-05 — PRD §4.6).
 *
 * 4 passos numerados do processo Omanna (Conversamos → Desenhamos →
 * Desenvolvemos → Entregamos). Layout:
 *  - Desktop (lg+): horizontal — 4 cols inline, com linha conectora sutil
 *    entre os steps (pseudo-elemento via `lg:before:` na lista)
 *  - Mobile/tablet: vertical stack, com linha conectora vertical a esquerda
 *
 * Background: `bg-neutral-50` (alternancia com Servicos `bg-white`).
 * Anchor: `#como-funciona` (acessado pelo menu Como funciona do Navbar e
 * pelo secondary CTA do Hero).
 *
 * Cada step e:
 *  - Numero em circle bg-brand-500 text-white (badge com font-display)
 *  - Titulo (font-display, bold, neutral-800)
 *  - Descricao (font-sans, neutral-600)
 *
 * Reveal: header + 4 steps com stagger CSS-only de 130ms.
 */
export const ComoFunciona = (): JSX.Element => {
  const { comoFunciona } = landingCopy;

  return (
    <section
      id="como-funciona"
      aria-labelledby="como-funciona-heading"
      className="bg-neutral-50 py-20 lg:py-28"
    >
      <Container>
        <RevealOnView>
          <SectionHeading
            as="h2"
            eyebrow={comoFunciona.eyebrow}
            title={comoFunciona.title}
            align="left"
            className="max-w-3xl"
            titleId="como-funciona-heading"
          />
        </RevealOnView>

        <ol className="relative mt-14 grid gap-10 lg:grid-cols-4 lg:gap-8">
          {/* Linha conectora horizontal (desktop) — sutil, atras dos badges */}
          <div
            aria-hidden="true"
            className="absolute left-6 top-6 hidden h-px w-[calc(100%-3rem)] bg-gradient-to-r from-brand-200 via-brand-300 to-brand-200 lg:block"
          />
          {/* Linha conectora vertical (mobile) — atras da coluna de badges */}
          <div
            aria-hidden="true"
            className="absolute left-6 top-6 bottom-6 w-px bg-gradient-to-b from-brand-200 via-brand-300 to-brand-200 lg:hidden"
          />

          {comoFunciona.steps.map((step, index) => (
            <RevealOnView key={step.number} delayMs={STAGGER_DELAY_MS * (index + 1)}>
              <li className="relative grid grid-cols-[auto_1fr] gap-4 lg:block lg:gap-0">
                <div className="relative z-10 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 font-display text-base font-bold text-white shadow-md ring-4 ring-neutral-50">
                  {step.number}
                </div>
                <div className="lg:mt-5">
                  <h3 className="font-display text-xl font-bold text-neutral-800">{step.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-neutral-600">
                    {step.description}
                  </p>
                </div>
              </li>
            </RevealOnView>
          ))}
        </ol>
      </Container>
    </section>
  );
};
